import * as React from 'react';
import { Loader2, Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SearchableSelect from '@/components/common/SearchableSelect';
import AdvisorCardGrid, { DirectoryEntry } from '@/components/advisor/public/AdvisorCardGrid';

export function splitCsv(value: string): string[] {
  return value ? value.split(',').map((v) => v.trim()).filter(Boolean) : [];
}

interface Props {
  advisors: DirectoryEntry[];
  loading?: boolean;
  onSelect?: (advisor: DirectoryEntry) => void;
  selectedId?: string | null;
  emptyMessage?: string;
  columns?: 'auto' | 'single';
}

export default function AdvisorDirectory({
  advisors,
  loading = false,
  onSelect,
  selectedId,
  emptyMessage = 'No advisors listed yet.',
  columns = 'auto',
}: Props) {
  const [search, setSearch] = React.useState('');
  const [language, setLanguage] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [city, setCity] = React.useState('');
  const [industry, setIndustry] = React.useState('');

  const options = React.useMemo(() => {
    const languages = new Set<string>();
    const countries = new Set<string>();
    const cities = new Set<string>();
    const industries = new Set<string>();
    for (const advisor of advisors) {
      splitCsv(advisor.languages).forEach((l) => languages.add(l));
      if (advisor.country) countries.add(advisor.country);
      splitCsv(advisor.cities).forEach((c) => cities.add(c));
      splitCsv(advisor.industries).forEach((i) => industries.add(i));
    }
    return {
      languages: [...languages].sort(),
      countries: [...countries].sort(),
      cities: [...cities].sort(),
      industries: [...industries].sort(),
    };
  }, [advisors]);

  const hasFilters = Boolean(language || country || city || industry);

  const filtered = advisors.filter((advisor) => {
    if (search) {
      const needle = search.toLowerCase();
      const matches =
        (advisor.display_name || '').toLowerCase().includes(needle) ||
        (advisor.headline || '').toLowerCase().includes(needle) ||
        (advisor.industries || '').toLowerCase().includes(needle) ||
        (advisor.specializations || '').toLowerCase().includes(needle);
      if (!matches) return false;
    }
    if (language) {
      const theirs = splitCsv(advisor.languages).map((l) => l.toLowerCase());
      if (!theirs.includes(language.toLowerCase())) return false;
    }
    if (country && (advisor.country || '').toLowerCase() !== country.toLowerCase()) {
      return false;
    }
    if (city) {
      const theirs = splitCsv(advisor.cities).map((c) => c.toLowerCase());
      if (!theirs.includes(city.toLowerCase())) return false;
    }
    if (industry) {
      const theirs = splitCsv(advisor.industries).map((i) => i.toLowerCase());
      if (!theirs.includes(industry.toLowerCase())) return false;
    }
    return true;
  });

  const select = (
    value: string,
    setValue: (next: string) => void,
    label: string,
    values: string[],
    testId: string,
  ) => (
    <SearchableSelect
      className="h-9 w-auto min-w-[10rem]"
      value={value}
      onChange={setValue}
      placeholder={label}
      searchPlaceholder={`Search ${label.replace(/^All /, '').toLowerCase()}…`}
      options={[{ value: '', label }, ...values.map((option) => ({ value: option, label: option }))]}
      testId={testId}
    />
  );

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name, headline, industry or specialism…"
          className="pl-9"
          data-testid="advisor-search"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {select(language, setLanguage, 'All Languages', options.languages, 'filter-language')}
        {select(country, setCountry, 'All Countries', options.countries, 'filter-country')}
        {select(city, setCity, 'All Cities', options.cities, 'filter-city')}
        {select(industry, setIndustry, 'All Industries', options.industries, 'filter-industry')}
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 text-xs"
            onClick={() => {
              setLanguage('');
              setCountry('');
              setCity('');
              setIndustry('');
            }}
            data-testid="clear-filters"
          >
            <X className="mr-1 h-3 w-3" /> Clear
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="py-12 text-center text-muted-foreground" data-testid="advisor-directory-empty">
          {search || hasFilters ? 'No advisors match your filters.' : emptyMessage}
        </p>
      ) : (
        <AdvisorCardGrid advisors={filtered} onSelect={onSelect} selectedId={selectedId} columns={columns} />
      )}
    </div>
  );
}
